import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RefreshCw } from "lucide-react";
import { Field, TextInput, SectionTitle } from "./Field";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

export const EmailSettingsEditor = ({ token }) => {
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("25");
  const [smtpUsername, setSmtpUsername] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [smtpFromName, setSmtpFromName] = useState("");
  const [adminNotificationEmail, setAdminNotificationEmail] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings/email`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSmtpHost(data.smtp_host || "");
      setSmtpPort(String(data.smtp_port || "25"));
      setSmtpUsername(data.smtp_username || "");
      setSmtpPassword(data.smtp_password || "");
      setSmtpFromEmail(data.smtp_from_email || "");
      setSmtpFromName(data.smtp_from_name || "");
      setAdminNotificationEmail(data.admin_notification_email || "");
      setDirty(false);
    } catch {
      toast.error("Gabim në ngarkimin e cilësimeve të emailit.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (setter) => (val) => {
    setter(val);
    setDirty(true);
  };

  const save = async (e) => {
    if (e) e.preventDefault();
    
    // Quick validation
    if (!smtpHost || !smtpPort || !smtpFromEmail || !smtpFromName || !adminNotificationEmail) {
      toast.error("Ju lutem plotësoni të gjitha fushat e detyrueshme.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort, 10),
        smtp_username: smtpUsername,
        smtp_password: smtpPassword,
        smtp_from_email: smtpFromEmail,
        smtp_from_name: smtpFromName,
        admin_notification_email: adminNotificationEmail,
      };
      await axios.put(`${API}/admin/settings/email`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cilësimet e emailit u ruajtën me sukses.");
      setDirty(false);
      
      // Reload settings to get the masked password placeholder back
      fetchSettings();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : "Gabim në ruajtje. Kontrolloni të dhënat.";
      toast.error(errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm-muted py-20 text-center">Duke ngarkuar cilësimet e emailit...</p>;
  }

  return (
    <div data-testid="admin-email-settings">
      {/* Action Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-sm-border">
        <SectionTitle sub="Konfiguroni serverin SMTP dhe adresat e emailit për njoftime dhe konfirmime.">
          Konfigurimi i Emailit
        </SectionTitle>
        <div className="flex items-center gap-3">
          {dirty && (
            <span
              data-testid="email-dirty-indicator"
              className="text-[0.65rem] tracking-[0.28em] uppercase text-sm-terra"
            >
              · Pa ruajtur
            </span>
          )}
          <button
            type="button"
            onClick={fetchSettings}
            disabled={saving}
            className="inline-flex items-center gap-2 border border-sm-border px-4 py-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive hover:border-sm-olive transition-colors disabled:opacity-60"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Rifresko
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="email-save-btn"
            className="inline-flex items-center gap-2 bg-sm-text text-sm-bg px-5 py-2 text-[0.7rem] tracking-[0.28em] uppercase font-medium hover:bg-sm-olive transition-colors disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "Duke ruajtur..." : "Ruaj"}
          </button>
        </div>
      </div>

      <form onSubmit={save} className="bg-sm-surface border border-sm-border p-6 md:p-10 space-y-6">
        <h4 className="font-serif text-lg text-sm-text pb-2 border-b border-sm-border/60">
          Serveri SMTP
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="SMTP Host *">
            <TextInput
              value={smtpHost}
              onChange={handleFieldChange(setSmtpHost)}
              placeholder="p.sh. smtp.gmail.com ose localhost"
              testid="email-smtp-host"
            />
          </Field>
          
          <Field label="SMTP Port *">
            <TextInput
              value={smtpPort}
              onChange={handleFieldChange(setSmtpPort)}
              placeholder="p.sh. 587 ose 465 ose 25"
              testid="email-smtp-port"
            />
          </Field>
          
          <Field label="SMTP Përdoruesi (Username)">
            <TextInput
              value={smtpUsername}
              onChange={handleFieldChange(setSmtpUsername)}
              placeholder="p.sh. emri@gmail.com"
              testid="email-smtp-username"
            />
          </Field>
          
          <Field label="SMTP Fjalëkalimi (Password)">
            <input
              type="password"
              value={smtpPassword}
              onChange={(e) => handleFieldChange(setSmtpPassword)(e.target.value)}
              placeholder={smtpPassword ? "******" : "Fjalëkalimi ose App Password"}
              className="sm-admin-input w-full"
              data-testid="email-smtp-password"
            />
          </Field>
        </div>

        <h4 className="font-serif text-lg text-sm-text pt-4 pb-2 border-b border-sm-border/60">
          Adresat & Dërguesi
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Emri i Dërguesit *">
            <TextInput
              value={smtpFromName}
              onChange={handleFieldChange(setSmtpFromName)}
              placeholder="p.sh. San Martino 26"
              testid="email-from-name"
            />
          </Field>
          
          <Field label="Email-i i Dërguesit (From Email) *">
            <TextInput
              value={smtpFromEmail}
              onChange={handleFieldChange(setSmtpFromEmail)}
              placeholder="p.sh. rezervo@sanmartino26.al"
              testid="email-from-email"
            />
          </Field>

          <Field label="Email për Njoftimet e Adminit *">
            <TextInput
              value={adminNotificationEmail}
              onChange={handleFieldChange(setAdminNotificationEmail)}
              placeholder="Email ku admini merr njoftimet e rezervimeve"
              testid="email-admin-notify"
            />
          </Field>
        </div>

        <div className="text-[0.65rem] text-sm-muted uppercase tracking-wider pt-2">
          * Fushat e shënuara me yll janë të detyrueshme.
        </div>
      </form>
    </div>
  );
};

export default EmailSettingsEditor;
